import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import InputAdornment from '@mui/material/InputAdornment'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { formatPhone } from '@jupi/shared'
import { api, RemoteContent, Title, UserAvatar } from '@jupi/ui'
import { queryKeys, type Member } from '../api/queries'

export default function MembersPage() {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')

  const { data: members = [], isLoading, error } = useQuery({
    queryKey: queryKeys.members,
    queryFn: () => api.get<{ members: Member[] }>('/api/members').then((r) => r.members),
  })

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return members
    return members.filter((m) =>
      [m.displayName, m.nickname, m.memberNumber].filter(Boolean).some((v) => v!.toLowerCase().includes(needle)),
    )
  }, [members, search])

  return (
    <Box>
      <Title label={t('member.titlePlural')} />

      <TextField
        fullWidth
        size="small"
        placeholder={t('common.search')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <Icon icon="mdi:magnify" />
              </InputAdornment>
            ),
          },
        }}
      />

      <RemoteContent isLoading={isLoading} error={error}>
        <Stack spacing={1}>
          {filtered.map((member) => (
            <Card key={member.userId} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 }, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <UserAvatar
                  userId={member.userId}
                  avatarVersion={member.avatarVersion}
                  email={member.email ?? ''}
                  displayName={member.displayName}
                  size={40}
                />
                <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {member.displayName}
                  {member.nickname ? (
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      “{member.nickname}”
                    </Typography>
                  ) : null}
                  {member.memberNumber ? (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      #{member.memberNumber}
                    </Typography>
                  ) : null}
                </Typography>

                {member.detailsHidden ? (
                  <Typography variant="caption" color="text.secondary">
                    {t('member.detailsHidden')}
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
                    {member.phone ? (
                      // tel: and mailto: are the point of this page on a
                      // phone -- you are looking someone up to call them.
                      <Link href={`tel:${member.phone}`} variant="body2" underline="hover">
                        {formatPhone(member.phone)}
                      </Link>
                    ) : null}
                    {member.email ? (
                      <Link href={`mailto:${member.email}`} variant="body2" underline="hover">
                        {member.email}
                      </Link>
                    ) : null}
                  </Stack>
                )}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      </RemoteContent>
    </Box>
  )
}
